import { get, groupBy, isArray } from 'lodash';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';

import { Folder } from '~/eds/src/components/Folder/Folder';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { Text } from '~/eds/src/components/Text/Text';
import { DEFAULT_PLACEHOLDER_LABEL } from '~/eds/src/constants/strings';
import { parseDate } from '~/eds/src/utils/parseDate';
import { FieldId } from '~/evifields/src/types/FieldId';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import type { useFormatDateReturnType } from '~/i18n/hooks/useFormatDate/types';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { SearchItem } from '~/redux/api/methods/evisearch';
import { SearchFilter } from '~/types/DashboardV2';

type Props = {
  staticColumns: Array<any>;
  searchFilters: Record<FieldId, SearchFilter>;
};

export const useEvisearchColumns = ({
  staticColumns,
  searchFilters,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  const searchFiltersList = Object.values(searchFilters)
    .filter((filter) => filter.is_requestable_column)
    .map((searchFilter) =>
      fieldToColumn(searchFilter, intl, formatDate, hasLocalization),
    );

  const dynamicColumns = searchFiltersList.filter(
    (column) =>
      !staticColumns.some((staticColumn) => staticColumn.key === column.key),
  );
  const columns = [...staticColumns, ...dynamicColumns];
  const columnGroups = Object.entries(
    groupBy(
      columns.filter((column) => column.section),
      'section',
    ),
  ).map(([section, group]) => ({
    name: section,
    label: section,
    columns: group.map((column) => column.key),
  }));

  return { columns, columnGroups };
};

const fieldToColumn = (
  searchFilter: SearchFilter,
  intl: any,
  formatDate: useFormatDateReturnType,
  hasLocalization: boolean,
) => {
  switch (searchFilter.id) {
    case 'document_group_Document':
      return {
        key: searchFilter.id,
        title: searchFilter.label,
        minWidth: 'm',
        disableSortBy: true,
        section: searchFilter.section,
        renderCell: getGroupCell,
      };
    case 'folder_Document':
      return {
        key: searchFilter.id,
        title: searchFilter.label,
        minWidth: 'm',
        disableSortBy: true,
        section: searchFilter.section,
        renderCell: (document: SearchItem) => getFolderCell(document, intl),
      };

    default:
      return {
        key: searchFilter.id,
        title: searchFilter.label,
        disableSortBy: false,
        section: searchFilter.section,
        cellType: getCellType(searchFilter.type),
        mapCellProps: (searchItem: SearchItem) => {
          const value = get(
            searchItem,
            `selected_field_values[${searchFilter.id}].value`,
            [],
          );
          const rawValue = isArray(value) ? value.join(', ') : value;
          return {
            text: getText(formatDate, rawValue, intl, hasLocalization),
            datetime:
              isDate(rawValue) && !isNumber(rawValue)
                ? parseDate(rawValue, true)
                : undefined,
          };
        },
      };
  }
};

export const getGroupCell = (document: SearchItem) => {
  const group = get(
    document,
    `selected_field_values[document_group_Document].value_structured[0]`,
    {},
  );

  return group.id ? (
    <Layout align="center" spacing={2}>
      <Icon icon="groups" role="presentation" />
      <Link pathname={`/document-group/${group.id}`}>{group.name}</Link>
    </Layout>
  ) : (
    DEFAULT_PLACEHOLDER_LABEL
  );
};

export const getFolderCell = (document: SearchItem, intl: IntlShape) => {
  const handlers = get(
    document,
    'selected_field_values.document_handler_id_Document.value_structured',
    [],
  );
  if (handlers?.length > 1) {
    return (
      <Layout align="center" spacing={2}>
        <Icon icon="folder" role="presentation" />
        <Text>
          {intl.formatMessage({
            id: 'useEvisearchColumns.multipleLocations',
            defaultMessage: 'Multiple Locations',
            description: 'Label for documents in multiple folder locations',
          })}
        </Text>
      </Layout>
    );
  } else {
    const folder = get(
      document,
      'selected_field_values.folder_Document.value_structured[0]',
    );
    return folder ? (
      <Folder
        link={{
          pathname: `/documents/${folder.id}`,
        }}
        path={folder.name}
        name={folder.name}
      />
    ) : (
      DEFAULT_PLACEHOLDER_LABEL
    );
  }
};

const isDate = (value: string) => {
  return !isNaN(Date.parse(value));
};
const isNumber = (value: string) => {
  return !isNaN(Number(value));
};

const isBoolean = (value: string) => {
  return value === 'true' || value === 'false';
};

export const getText = (
  formatDate: useFormatDateReturnType,
  value: string,
  intl: any,
  hasLocalization: boolean,
) => {
  if (!value) return DEFAULT_PLACEHOLDER_LABEL;

  if (isNumber(value)) {
    return value;
  } else if (isDate(value)) {
    return formatDate(value, hasLocalization ? 'short' : 'iso');
  } else if (isBoolean(value)) {
    return value === 'true'
      ? intl.formatMessage({
          id: 'useEvisearchColumns.yes',
          defaultMessage: 'Yes',
          description: 'Positive boolean value',
        })
      : intl.formatMessage({
          id: 'useEvisearchColumns.no',
          defaultMessage: 'No',
          description: 'Negative boolean value',
        });
  }
  return value;
};

export const getCellType = (value: string) => {
  switch (value) {
    case 'date':
      return 'datetime';

    default:
      return 'text';
  }
};
